const toPositiveNumber = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return 0;
  }

  return numericValue;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const getMapRenderSize = ({ sourceWidth, sourceHeight, containerWidth, containerHeight }) => {
  const safeSourceWidth = toPositiveNumber(sourceWidth);
  const safeSourceHeight = toPositiveNumber(sourceHeight);
  const safeContainerWidth = toPositiveNumber(containerWidth);
  const safeContainerHeight = toPositiveNumber(containerHeight);

  if (!safeSourceWidth || !safeSourceHeight) {
    return { width: 0, height: 0, scale: 0 };
  }

  if (!safeContainerWidth && !safeContainerHeight) {
    return { width: safeSourceWidth, height: safeSourceHeight, scale: 1 };
  }

  if (safeContainerWidth && !safeContainerHeight) {
    const scale = safeContainerWidth / safeSourceWidth;

    return {
      width: safeSourceWidth * scale,
      height: safeSourceHeight * scale,
      scale,
    };
  }

  if (!safeContainerWidth && safeContainerHeight) {
    const scale = safeContainerHeight / safeSourceHeight;

    return {
      width: safeSourceWidth * scale,
      height: safeSourceHeight * scale,
      scale,
    };
  }

  const scale = Math.min(safeContainerWidth / safeSourceWidth, safeContainerHeight / safeSourceHeight);

  return {
    width: safeSourceWidth * scale,
    height: safeSourceHeight * scale,
    scale,
  };
};

export const getCalibrationViewport = ({ mapWidth, mapHeight, calibration }) => {
  const safeMapWidth = toPositiveNumber(mapWidth);
  const safeMapHeight = toPositiveNumber(mapHeight);

  if (!safeMapWidth || !safeMapHeight) {
    return { left: 0, top: 0, width: 0, height: 0, bottom: 0 };
  }

  const leftRatio = clamp((Number(calibration?.offsetLeftPercent) || 0) / 100, 0, 1);
  const topRatio = clamp((Number(calibration?.offsetTopPercent) || 0) / 100, 0, 1);
  const widthRatio = clamp((Number(calibration?.activeWidthPercent) || 100) / 100, 0, 1 - leftRatio);
  const heightRatio = clamp((Number(calibration?.activeHeightPercent) || 100) / 100, 0, 1 - topRatio);

  const left = leftRatio * safeMapWidth;
  const top = topRatio * safeMapHeight;
  const width = widthRatio * safeMapWidth;
  const height = heightRatio * safeMapHeight;

  return {
    left,
    top,
    width,
    height,
    bottom: safeMapHeight - top - height,
  };
};

/**
 * Помещается ли точка в заявленные размеры этажа.
 *
 * 🔴 Правило живёт В ОДНОМ месте намеренно: его спрашивают двое — сама точка
 * (чтобы пометить себя) и карта (чтобы посчитать их и сказать человеку). Две
 * копии условия разошлись бы на первой же правке, и карта считала бы одно, а
 * рисовала другое.
 *
 * Границы ВКЛЮЧИТЕЛЬНЫ: точка ровно у стены стоит там законно. Неизвестная
 * координата (мусор вместо числа) — не «вне этажа», а «неизвестно где»:
 * обвинять данные, которых не видели, нельзя.
 */
export const isOutsidePlan = ({ x, y, planWidth, planHeight }) => {
  const width = toPositiveNumber(planWidth);
  const height = toPositiveNumber(planHeight);
  const safeX = Number(x);
  const safeY = Number(y);

  if (!width || !height) return false;
  if (!Number.isFinite(safeX) || !Number.isFinite(safeY)) return false;

  return safeX < 0 || safeX > width || safeY < 0 || safeY > height;
};

/**
 * Координаты в метрах -> место на отрисованном плане.
 *
 * 🔴 WEB-18: возвращает ещё и `clamped` — правда ли точка НЕ ПОМЕСТИЛАСЬ в
 * заявленные размеры этажа. Зажим существовал и раньше (иначе точка уехала бы
 * за картинку), но был МОЛЧАЛИВЫМ: точка с x = 9,9 при этаже шириной 9 м
 * прижималась к правому краю плана и выглядела как точка, честно стоящая у
 * стены. На бою так стоят три точки из пяти, и по этим же координатам считается
 * позиция человека. Само по себе число здесь не чинится — оно приходит из базы;
 * но молчать о нём карта больше не имеет права.
 */
export const getPointPosition = ({ x, y, planWidth, planHeight, mapWidth, mapHeight, calibration }) => {
  const safePlanWidth = toPositiveNumber(planWidth);
  const safePlanHeight = toPositiveNumber(planHeight);
  const safeX = Number(x);
  const safeY = Number(y);
  const viewport = getCalibrationViewport({ mapWidth, mapHeight, calibration });

  if (!safePlanWidth || !safePlanHeight || !viewport.width || !viewport.height) {
    return { left: 0, bottom: 0, clamped: false };
  }

  const xRatio = clamp(safeX / safePlanWidth, 0, 1);
  const yRatio = clamp(safeY / safePlanHeight, 0, 1);
  const mappedYRatio = calibration?.invertY ? 1 - yRatio : yRatio;

  return {
    left: viewport.left + xRatio * viewport.width,
    bottom: viewport.bottom + mappedYRatio * viewport.height,
    clamped: isOutsidePlan({
      x, y, planWidth: safePlanWidth, planHeight: safePlanHeight,
    }),
  };
};
